import './ConfirmBox.css';
import { observer } from 'mobx-react';
import { action, makeAutoObservable, observable } from 'mobx';
import { Appl } from '../../Appl';
import { Button } from '../actions/Button';
import parse from 'html-react-parser';
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';

export const ConfirmBox: React.FC = observer(() => {
    const vm = Appl.ConfirmBox;
    const dialogRef = useRef<HTMLDivElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);
    let titleBackground = 'text-bg-info';
    let titleColor = 'black';
    let iconName = 'circle-info';

    useEffect(() => {
        if (vm.visible) {
            // Store the previously focused element
            previousFocusRef.current = document.activeElement as HTMLElement;
            
            // Focus the dialog when it opens
            if (dialogRef.current) {
                dialogRef.current.focus();
            }
        } else if (previousFocusRef.current) {
            // Restore focus when dialog closes
            previousFocusRef.current.focus();
        }
    }, [vm.visible]);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!vm.visible) return;

        if (event.key === 'Escape') {
            vm.acceptConfirmation(false);
            return;
        }

        if (event.key === 'Tab') {
            const dialog = dialogRef.current;
            if (!dialog) return;

            const focusableElements = dialog.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const firstFocusable = focusableElements[0] as HTMLElement;
            const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

            if (event.shiftKey) {
                if (document.activeElement === firstFocusable) {
                    event.preventDefault();
                    lastFocusable.focus();
                }
            } else {
                if (document.activeElement === lastFocusable) {
                    event.preventDefault();
                    firstFocusable.focus();
                }
            }
        }
    };

    return (
        <>
            {vm.visible &&
                <>
                    <div className="fixed inset-0 z-10 bg-gray-500 bg-opacity-75" style={{ zIndex: 4000 }}></div>
                    <div 
                        ref={dialogRef}
                        className="fixed inset-0 z-10 w-screen overflow-y-auto drop-shadow-sm" 
                        style={{ zIndex: 5000 }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="confirm-dialog-title"
                        aria-describedby="confirm-dialog-description"
                        tabIndex={-1}
                        onKeyDown={handleKeyDown}>
                        <div className="flex min-h-full items-end justify-center p-6 text-center sm:items-center sm:p-0">
                            <div className={`relative transform overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl ${vm.isSquareCorner ? '' : 'rounded-lg'}`}>
                                <div className="flex items-start bg-cream-50 border-b-2 border-orange-500 p-0 py-4 px-6 justify-between items-center">
                                    <h3 id="confirm-dialog-title" className="text-2xl font-semibold leading-6 text-black tracking--1percent">{vm.title}</h3>
                                    <div className='ml-4'>
                                        <button 
                                            type="button" 
                                            className="bg-orange-500 flex focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400 h-6 w-6 hover:text-white-500 inline-flex items-center justify-center rounded-full text-white" 
                                            data-pc-section="closebutton"
                                            onClick={() => { vm.acceptConfirmation(false) }}>
                                            <span className="sr-only">Close</span>
                                            <X className="h-4 w-4" aria-hidden="true" />
                                        </button>
                                    </div>
                                </div>
                                <div className="bg-white px-6 p-6">
                                    <div className="sm:flex sm:items-start">
                                        <div className="text-center sm:text-left">
                                            <div>
                                                <p id="confirm-dialog-description" className="text-15px text-gray-800">{parse(vm.message)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center gap-x-4 px-6 py- pb-6">
                                    <Button 
                                        title={vm.acceptLabel}
                                        rounded={vm.isSquareCorner}
                                        onClick={() => vm.acceptConfirmation(true)} />
                                    <Button 
                                        title={vm.rejectLabel} 
                                        severity='secondary'
                                        rounded={vm.isSquareCorner}
                                        onClick={() => vm.acceptConfirmation(false)} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            }
        </>
    );
});

export interface IConfirmBoxService {
    title: string;
    subTitle: string;
    message: string;
    visible: boolean;
    isSquareCorner?: boolean;
    confirmAccept: boolean;
    acceptLabel: string;
    rejectLabel: string;
    selectedObject?: any;
    show(message: string, title?: string, subTitle?: string, acceptLabel?: string, rejectLabel?: string, accept?: Function, reject?: Function, isSquareCorner?: boolean): void;
    hide(): void;
    acceptConfirmation(value: boolean): void;
    accept?: Function;
    reject?: Function;
}

export class ConfirmBoxService implements IConfirmBoxService {

    private static _instance: ConfirmBoxService;
    public static get instance() {
        return this._instance ?? (this._instance = new ConfirmBoxService());
    }

    @observable public title = '';
    @observable public subTitle = '';
    @observable public message = '';
    @observable public visible = false;
    @observable public acceptLabel = 'Yes';
    @observable public rejectLabel = 'No';
    @observable public confirmAccept = false;
    @observable public isSquareCorner? = false;
    accept?: Function;
    reject?: Function;
    constructor() {
        makeAutoObservable(this);
    }

    @action public show(message: string, title: string | undefined = 'Confirm', subTitle = '',
        acceptLabel = "Yes", rejectLabel = "No", accept?: Function, reject?: Function, isSquareCorner?: false): void {
        this.title = title;
        this.message = message;
        this.subTitle = subTitle;
        this.visible = true;
        this.confirmAccept = false;
        this.acceptLabel = acceptLabel;
        this.rejectLabel = rejectLabel;
        this.isSquareCorner = isSquareCorner;
        if (accept) {
            this.accept = accept;
        }
        if (reject) {
            this.reject = reject;
        }
    }

    @action public hide(): void {
        this.title = '';
        this.message = '';
        this.subTitle = '';
        this.visible = false;
    }

    @action public acceptConfirmation(value: boolean): void {
        this.confirmAccept = value;
        if (value) {
            if (this.accept) {
                this.accept();
            }
        } else {
            if (this.reject) {
                this.reject();
            }
        }
        this.hide();
        console.log(`acceptConfirmation:${this.confirmAccept}`);
    }
}